import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from '@/lib/platform';

import { ticketsApi } from '@/lib/api';
import { ridesApi } from '@/lib/rides-api';
import { passesApi } from '@/lib/passes-api';
import { queryKeys, rideKeys, passKeys } from '@/lib/query-keys';
import { formatDateTime } from '@/lib/format';
import { Badge } from '@/components/shared/badge';
import { ErrorState } from '@/components/shared/error-state';
import { Skeleton } from '@/components/shared/skeleton';
import { Screen } from '@/components/chrome';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/_authed/support/$category/$issueId')({
  component: IssueDetailPage,
});

// Both facades expose the same IGM shape; normalise into one view model so the
// screen is category-agnostic and dispatches to the right API by route param.
interface IssueView {
  description: string;
  category?: string;
  createdAt?: string;
  lastAction?: string;
  level: string;
  status: string;
  resolutions: {
    id?: string;
    code?: string;
    shortDesc?: string;
    longDesc?: string;
    actionTriggered?: string;
    refundAmount?: string;
    resolutionProvider?: string;
  }[];
}

function IssueDetailPage() {
  const { category: rawCategory, issueId } = Route.useParams();
  const navigate = useNavigate();
  // Dispatch to the right IGM facade by route param (deep-link / cold-load safe). All
  // three facades expose an identical issue/update/escalate/close surface.
  const api =
    rawCategory === 'rides' ? ridesApi : rawCategory === 'passes' ? passesApi : ticketsApi;
  const keys = rawCategory === 'rides' ? rideKeys : rawCategory === 'passes' ? passKeys : queryKeys;
  const supportLabel =
    rawCategory === 'rides'
      ? 'Ride support'
      : rawCategory === 'passes'
        ? 'Entry-pass support'
        : rawCategory === 'bus'
          ? 'Bus support'
          : 'Metro support';
  const qc = useQueryClient();

  const [info, setInfo] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionNote, setActionNote] = useState('');

  // Normalise inside the queryFn, not after it. `api` is a 3-way union of the
  // metro/rides/passes facades, so `api.issue()` returns a union of promises that
  // useQuery cannot collapse into a single QueryFunction — annotating the result
  // here resolves the union once and keeps the screen category-agnostic.
  const {
    data: issue,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<IssueView>({
    queryKey: keys.issue(issueId),
    queryFn: async () => {
      const d = await api.issue(issueId);
      return {
        description: d.description,
        category: d.category,
        createdAt: d.createdAt,
        lastAction: d.lastAction,
        level: d.level,
        status: d.status,
        resolutions: d.resolutions ?? [],
      };
    },
  });

  function invalidate() {
    void qc.invalidateQueries({ queryKey: keys.issue(issueId) });
    void qc.invalidateQueries({ queryKey: keys.allIssues() });
  }
  function startAction() {
    setActionError('');
    setActionNote('');
  }

  const updateMut = useMutation({
    mutationFn: () => api.updateIssue(issueId, { actionCode: 'INFO_PROVIDED', shortDesc: info }),
    onMutate: startAction,
    onSuccess: () => {
      setInfo('');
      setActionNote('Information sent to the operator.');
      invalidate();
    },
    onError: (e) => setActionError(getErrorMessage(e)),
  });
  const escalateMut = useMutation({
    mutationFn: () => api.escalateIssue(issueId, { shortDesc: 'Escalating to grievance' }),
    onMutate: startAction,
    onSuccess: () => {
      setActionNote('Escalated to a grievance.');
      invalidate();
    },
    onError: (e) => setActionError(getErrorMessage(e)),
  });
  const closeMut = useMutation({
    mutationFn: (rating: string) => api.closeIssue(issueId, { rating }),
    onMutate: startAction,
    onSuccess: () => {
      setActionNote('Close request sent.');
      invalidate();
    },
    onError: (e) => setActionError(getErrorMessage(e)),
  });
  // Accept / reject a seller-proposed resolution (IGM 2.0).
  const resolveMut = useMutation({
    mutationFn: (accepted: boolean) => {
      const body = {
        actionCode: accepted ? 'RESOLUTION_ACCEPTED' : 'RESOLUTION_REJECTED',
        shortDesc: accepted ? 'Resolution accepted by customer' : 'Resolution rejected by customer',
      };
      return api.updateIssue(issueId, body);
    },
    onMutate: startAction,
    onSuccess: (_d, accepted) => {
      setActionNote(
        accepted ? 'Resolution accepted.' : 'Resolution rejected — the operator will follow up.'
      );
      invalidate();
    },
    onError: (e) => setActionError(getErrorMessage(e)),
  });

  const back = () => void navigate({ to: '/support' });

  if (isLoading) {
    return (
      <Screen title="Issue" onBack={back}>
        <Skeleton w="100%" h={192} r={22} />
      </Screen>
    );
  }
  if (isError || !issue) {
    return (
      <Screen title="Issue" onBack={back}>
        <ErrorState error={error} onRetry={() => void refetch()} />
      </Screen>
    );
  }

  const closed = issue.status === 'CLOSED';
  const resolutions = issue.resolutions;

  return (
    <Screen title="Issue" onBack={back}>
      <div className="wb-col wb-gap-16">
        <div className="wb-row wb-between wb-gap-8" style={{ alignItems: 'flex-start' }}>
          <div className="wb-col wb-gap-4">
            <span className="wb-eyebrow">{supportLabel}</span>
            <h1 className="wb-h2">Issue</h1>
          </div>
          <div className="wb-row wb-gap-6" style={{ flexShrink: 0 }}>
            <Badge variant={issue.level === 'GRIEVANCE' ? 'red' : 'amber'}>{issue.level}</Badge>
            <Badge variant="default">{issue.status}</Badge>
          </div>
        </div>

        <div className="wb-card wb-card--pad">
          <p className="wb-body">{issue.description}</p>
          {issue.category && (
            <p className="wb-tiny" style={{ marginTop: 8 }}>
              Category: {issue.category}
            </p>
          )}
          <p className="wb-tiny" style={{ marginTop: 2 }}>
            Raised {formatDateTime(issue.createdAt)}
          </p>
        </div>

        {/* Seller requested more information (IGM INFO_REQUESTED). */}
        {issue.lastAction === 'INFO_REQUESTED' && !closed && (
          <div
            className="wb-card wb-card--pad wb-small"
            style={{ color: 'var(--honey-700)', background: 'var(--honey-wash-2)' }}
          >
            The operator has requested more information. Please add details below to help resolve
            your issue.
          </div>
        )}

        {/* Seller-proposed resolution(s) — accept or reject (IGM 2.0). */}
        {!closed && resolutions.length > 0 && (
          <div className="wb-card wb-card--pad wb-col wb-gap-8">
            <p className="wb-strong">
              {resolutions.length > 1 ? 'Proposed resolutions' : 'Proposed resolution'}
            </p>
            {resolutions.map((r, i) => (
              <div key={r.id || i} className="wb-inset" style={{ padding: 12 }}>
                <p className="wb-body">{r.shortDesc || r.code || 'Resolution offered'}</p>
                {r.longDesc && (
                  <p className="wb-tiny" style={{ marginTop: 4 }}>
                    {r.longDesc}
                  </p>
                )}
                {(r.actionTriggered || r.refundAmount || r.resolutionProvider) && (
                  <div className="wb-row wb-wrap wb-gap-8" style={{ marginTop: 8 }}>
                    {r.actionTriggered && (
                      <span className="wb-badge wb-badge--pending" style={{ fontSize: 11 }}>
                        {r.actionTriggered}
                      </span>
                    )}
                    {r.refundAmount && (
                      <span className="wb-small wb-strong">Refund: {r.refundAmount}</span>
                    )}
                    {r.resolutionProvider && (
                      <span className="wb-tiny">by {r.resolutionProvider}</span>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div className="wb-row wb-wrap wb-gap-8" style={{ paddingTop: 4 }}>
              <Button
                variant="primary"
                size="sm"
                onClick={() => resolveMut.mutate(true)}
                disabled={resolveMut.isPending}
              >
                Accept resolution
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => resolveMut.mutate(false)}
                disabled={resolveMut.isPending}
              >
                Reject
              </Button>
            </div>
          </div>
        )}

        {actionError && (
          <p className="wb-small" style={{ color: 'var(--danger)' }}>
            {actionError}
          </p>
        )}
        {actionNote && !actionError && (
          <p
            className="wb-small"
            style={{
              background: 'var(--surface-2)',
              borderRadius: 'var(--r-md)',
              padding: '10px 14px',
            }}
          >
            {actionNote}
          </p>
        )}

        {!closed && (
          <div className="wb-card wb-card--pad wb-col wb-gap-12">
            <div>
              <label className="wb-label" htmlFor="add-info">
                Add information
              </label>
              <textarea
                id="add-info"
                value={info}
                onChange={(e) => setInfo(e.target.value)}
                rows={3}
                placeholder="Add details for the operator…"
                className="wb-field wb-field--area"
              />
            </div>
            <div className="wb-row wb-wrap wb-gap-8">
              <Button
                variant="primary"
                size="sm"
                onClick={() => updateMut.mutate()}
                disabled={!info.trim() || updateMut.isPending}
              >
                {updateMut.isPending ? 'Sending…' : 'Add info'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                icon="escalate"
                onClick={() => escalateMut.mutate()}
                disabled={escalateMut.isPending || issue.level === 'GRIEVANCE'}
              >
                Escalate
              </Button>
            </div>

            <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12 }}>
              <p className="wb-small" style={{ marginBottom: 8 }}>
                Close this issue
              </p>
              <div className="wb-row wb-gap-8">
                <Button
                  variant="secondary"
                  size="sm"
                  icon="thumbUp"
                  onClick={() => closeMut.mutate('THUMBS-UP')}
                  disabled={closeMut.isPending}
                >
                  Resolved
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  icon="thumbDown"
                  onClick={() => closeMut.mutate('THUMBS-DOWN')}
                  disabled={closeMut.isPending}
                >
                  Not resolved
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Screen>
  );
}
